/**
 * AgentCenter - Track PDF ingestion jobs
 * Shows pending, processing, completed, and failed jobs with stats
 */

import React, { useEffect, useState, useCallback } from 'react';
import {
  Box,
  Typography,
  Paper,
  CircularProgress,
  Alert,
  Chip,
  Card,
  CardContent,
  Grid,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Tooltip,
  LinearProgress,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Collapse
} from '@mui/material';
import {
  Refresh as RefreshIcon,
  CheckCircle as CheckCircleIcon,
  Error as ErrorIcon,
  Pending as PendingIcon,
  Replay as ReplayIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  OpenInNew as OpenInNewIcon
} from '@mui/icons-material';
import { API_KEY } from '../config/api';

const API_BASE = typeof window !== 'undefined' && (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1')
  ? 'http://127.0.0.1:8000'
  : 'https://api.wallst.co.il';

interface Job {
  id: string;
  source_url: string | null;
  source_type: string;
  title: string | null;
  status: 'pending' | 'processing' | 'completed' | 'failed';
  stage: string | null;
  progress: number;
  document_id: string | null;
  chunks_count: number | null;
  semantic_units_count: number | null;
  pages_count: number | null;
  created_at: string;
  started_at: string | null;
  completed_at: string | null;
  error_message: string | null;
  error_stage: string | null;
  retry_count: number;
  duration_seconds: number | null;
  metadata: Record<string, unknown> | null;
}

interface Stats {
  total: number;
  pending: number;
  processing: number;
  completed: number;
  failed: number;
  avg_duration_seconds: number | null;
  success_rate: number | null;
}

const statusColors: Record<string, 'default' | 'primary' | 'success' | 'error' | 'warning'> = {
  pending: 'default',
  processing: 'primary',
  completed: 'success',
  failed: 'error'
};

const statusIcons: Record<string, React.ReactNode> = {
  pending: <PendingIcon fontSize="small" />,
  processing: <CircularProgress size={16} />,
  completed: <CheckCircleIcon fontSize="small" />,
  failed: <ErrorIcon fontSize="small" />
};

const AgentCenter: React.FC = () => {
  const [jobs, setJobs] = useState<Job[]>([]);
  const [stats, setStats] = useState<Stats | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [expandedJob, setExpandedJob] = useState<string | null>(null);
  const [autoRefresh, setAutoRefresh] = useState(true);

  const fetchData = useCallback(async () => {
    try {
      const [jobsRes, statsRes] = await Promise.all([
        fetch(`${API_BASE}/api/agent/jobs?limit=100&days=30${statusFilter !== 'all' ? `&status=${statusFilter}` : ''}`, {
          headers: { 'X-API-Key': API_KEY },
        }),
        fetch(`${API_BASE}/api/agent/jobs/stats?days=30`, {
          headers: { 'X-API-Key': API_KEY },
        })
      ]);

      if (!jobsRes.ok || !statsRes.ok) {
        throw new Error(`Unable to load ingestion jobs (${!jobsRes.ok ? jobsRes.status : statsRes.status})`);
      }

      const jobsData = await jobsRes.json();
      const statsData = await statsRes.json();

      setJobs(jobsData);
      setStats(statsData);
      setError(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Unable to load ingestion jobs');
    } finally {
      setLoading(false);
    }
  }, [statusFilter]);

  // Initial fetch
  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Auto-refresh every 10 seconds if enabled
  useEffect(() => {
    if (!autoRefresh) return;
    const interval = setInterval(fetchData, 10000);
    return () => clearInterval(interval);
  }, [autoRefresh, fetchData]);

  const handleRetry = async (jobId: string) => {
    try {
      const response = await fetch(`${API_BASE}/api/agent/jobs/${jobId}/retry`, {
        method: 'POST',
        headers: { 'X-API-Key': API_KEY },
      });
      if (!response.ok) throw new Error('Failed to retry job');
      fetchData();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to retry job');
    }
  };

  const formatDuration = (seconds: number | null) => {
    if (!seconds) return '-';
    if (seconds < 60) return `${seconds}s`;
    if (seconds < 3600) return `${Math.floor(seconds / 60)}m ${seconds % 60}s`;
    return `${Math.floor(seconds / 3600)}h ${Math.floor((seconds % 3600) / 60)}m`;
  };

  const formatDate = (dateStr: string | null) => {
    if (!dateStr) return '-';
    const date = new Date(dateStr);
    return date.toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '80vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3, maxWidth: 1400, mx: 'auto' }}>
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4">Agent Center</Typography>
        <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
          <Chip
            label={autoRefresh ? 'Auto-refresh ON' : 'Auto-refresh OFF'}
            color={autoRefresh ? 'success' : 'default'}
            onClick={() => setAutoRefresh(!autoRefresh)}
            size="small"
          />
          <IconButton onClick={fetchData} title="Refresh now">
            <RefreshIcon />
          </IconButton>
        </Box>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {/* Stats Cards */}
      {stats && (
        <Grid container spacing={2} sx={{ mb: 3 }}>
          <Grid item xs={6} sm={4} md={2}>
            <Card>
              <CardContent sx={{ textAlign: 'center', py: 2 }}>
                <Typography variant="h4" color="text.secondary">{stats.total}</Typography>
                <Typography variant="body2" color="text.secondary">Total Jobs</Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={6} sm={4} md={2}>
            <Card sx={{ borderLeft: '4px solid #9e9e9e' }}>
              <CardContent sx={{ textAlign: 'center', py: 2 }}>
                <Typography variant="h4" color="text.secondary">{stats.pending}</Typography>
                <Typography variant="body2" color="text.secondary">Pending</Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={6} sm={4} md={2}>
            <Card sx={{ borderLeft: '4px solid #2196f3' }}>
              <CardContent sx={{ textAlign: 'center', py: 2 }}>
                <Typography variant="h4" color="primary">{stats.processing}</Typography>
                <Typography variant="body2" color="text.secondary">Processing</Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={6} sm={4} md={2}>
            <Card sx={{ borderLeft: '4px solid #4caf50' }}>
              <CardContent sx={{ textAlign: 'center', py: 2 }}>
                <Typography variant="h4" color="success.main">{stats.completed}</Typography>
                <Typography variant="body2" color="text.secondary">Completed</Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={6} sm={4} md={2}>
            <Card sx={{ borderLeft: '4px solid #f44336' }}>
              <CardContent sx={{ textAlign: 'center', py: 2 }}>
                <Typography variant="h4" color="error">{stats.failed}</Typography>
                <Typography variant="body2" color="text.secondary">Failed</Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={6} sm={4} md={2}>
            <Card>
              <CardContent sx={{ textAlign: 'center', py: 2 }}>
                <Typography variant="h4" color="text.secondary">
                  {stats.success_rate !== null ? `${stats.success_rate}%` : '-'}
                </Typography>
                <Typography variant="body2" color="text.secondary">Success Rate</Typography>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      )}

      {/* Filter */}
      <Paper sx={{ p: 2, mb: 2 }}>
        <FormControl size="small" sx={{ minWidth: 150 }}>
          <InputLabel>Status Filter</InputLabel>
          <Select
            value={statusFilter}
            label="Status Filter"
            onChange={(e) => setStatusFilter(e.target.value)}
          >
            <MenuItem value="all">All Status</MenuItem>
            <MenuItem value="pending">Pending</MenuItem>
            <MenuItem value="processing">Processing</MenuItem>
            <MenuItem value="completed">Completed</MenuItem>
            <MenuItem value="failed">Failed</MenuItem>
          </Select>
        </FormControl>
      </Paper>

      {/* Jobs Table */}
      <TableContainer component={Paper}>
        <Table size="small">
          <TableHead>
            <TableRow sx={{ bgcolor: '#f5f5f5' }}>
              <TableCell width={40}></TableCell>
              <TableCell>Title / Source</TableCell>
              <TableCell width={120}>Status</TableCell>
              <TableCell width={150}>Progress</TableCell>
              <TableCell width={100}>Duration</TableCell>
              <TableCell width={130}>Created</TableCell>
              <TableCell width={80}>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {jobs.length === 0 ? (
              <TableRow>
                <TableCell colSpan={7} align="center" sx={{ py: 4 }}>
                  <Typography color="text.secondary">No jobs found</Typography>
                </TableCell>
              </TableRow>
            ) : (
              jobs.map((job) => (
                <React.Fragment key={job.id}>
                  <TableRow
                    hover
                    sx={{
                      cursor: 'pointer',
                      '&:hover': { bgcolor: '#fafafa' },
                      bgcolor: expandedJob === job.id ? '#f0f7ff' : 'inherit'
                    }}
                    onClick={() => setExpandedJob(expandedJob === job.id ? null : job.id)}
                  >
                    <TableCell>
                      <IconButton size="small">
                        {expandedJob === job.id ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                      </IconButton>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" fontWeight={500} noWrap sx={{ maxWidth: 300 }}>
                        {job.title || 'Untitled'}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {job.source_type}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Chip
                        icon={statusIcons[job.status] as React.ReactElement}
                        label={job.stage || job.status}
                        color={statusColors[job.status]}
                        size="small"
                        sx={{ textTransform: 'capitalize' }}
                      />
                    </TableCell>
                    <TableCell>
                      {job.status === 'processing' ? (
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <LinearProgress
                            variant="determinate"
                            value={job.progress}
                            sx={{ flexGrow: 1, height: 8, borderRadius: 4 }}
                          />
                          <Typography variant="caption">{job.progress}%</Typography>
                        </Box>
                      ) : job.status === 'completed' ? (
                        <Typography variant="body2" color="success.main">100%</Typography>
                      ) : job.status === 'failed' ? (
                        <Typography variant="body2" color="error">Failed</Typography>
                      ) : (
                        <Typography variant="body2" color="text.secondary">-</Typography>
                      )}
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">
                        {formatDuration(job.duration_seconds)}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">
                        {formatDate(job.created_at)}
                      </Typography>
                    </TableCell>
                    <TableCell onClick={(e) => e.stopPropagation()}>
                      {job.status === 'failed' && (
                        <Tooltip title="Retry job">
                          <IconButton
                            size="small"
                            color="primary"
                            onClick={() => handleRetry(job.id)}
                          >
                            <ReplayIcon />
                          </IconButton>
                        </Tooltip>
                      )}
                      {job.document_id && (
                        <Tooltip title="View document">
                          <IconButton
                            size="small"
                            color="primary"
                            onClick={() => window.open(`/document-pro?id=${job.document_id}`, '_blank')}
                          >
                            <OpenInNewIcon />
                          </IconButton>
                        </Tooltip>
                      )}
                    </TableCell>
                  </TableRow>

                  {/* Expanded Details */}
                  <TableRow>
                    <TableCell colSpan={7} sx={{ p: 0, border: 0 }}>
                      <Collapse in={expandedJob === job.id}>
                        <Box sx={{ p: 2, bgcolor: '#f8f9fa', borderBottom: '1px solid #e0e0e0' }}>
                          <Grid container spacing={2}>
                            <Grid item xs={12} md={6}>
                              <Typography variant="subtitle2" gutterBottom>Job Details</Typography>
                              <Box sx={{ display: 'grid', gridTemplateColumns: '120px 1fr', gap: 0.5 }}>
                                <Typography variant="caption" color="text.secondary">Job ID:</Typography>
                                <Typography variant="caption" sx={{ fontFamily: 'monospace' }}>{job.id}</Typography>

                                <Typography variant="caption" color="text.secondary">Source URL:</Typography>
                                <Typography variant="caption" noWrap sx={{ maxWidth: 300 }}>
                                  {job.source_url || '-'}
                                </Typography>

                                <Typography variant="caption" color="text.secondary">Document ID:</Typography>
                                <Typography variant="caption" sx={{ fontFamily: 'monospace' }}>
                                  {job.document_id || '-'}
                                </Typography>

                                <Typography variant="caption" color="text.secondary">Retry Count:</Typography>
                                <Typography variant="caption">{job.retry_count}</Typography>
                              </Box>
                            </Grid>

                            <Grid item xs={12} md={3}>
                              <Typography variant="subtitle2" gutterBottom>Processing Stats</Typography>
                              <Box sx={{ display: 'grid', gridTemplateColumns: '100px 1fr', gap: 0.5 }}>
                                <Typography variant="caption" color="text.secondary">Pages:</Typography>
                                <Typography variant="caption">{job.pages_count ?? '-'}</Typography>

                                <Typography variant="caption" color="text.secondary">Chunks:</Typography>
                                <Typography variant="caption">{job.chunks_count ?? '-'}</Typography>

                                <Typography variant="caption" color="text.secondary">Semantic Units:</Typography>
                                <Typography variant="caption">{job.semantic_units_count ?? '-'}</Typography>
                              </Box>
                            </Grid>

                            <Grid item xs={12} md={3}>
                              <Typography variant="subtitle2" gutterBottom>Timing</Typography>
                              <Box sx={{ display: 'grid', gridTemplateColumns: '80px 1fr', gap: 0.5 }}>
                                <Typography variant="caption" color="text.secondary">Started:</Typography>
                                <Typography variant="caption">{formatDate(job.started_at)}</Typography>

                                <Typography variant="caption" color="text.secondary">Completed:</Typography>
                                <Typography variant="caption">{formatDate(job.completed_at)}</Typography>
                              </Box>
                            </Grid>

                            {job.error_message && (
                              <Grid item xs={12}>
                                <Alert severity="error" sx={{ mt: 1 }}>
                                  <Typography variant="subtitle2">
                                    Error at stage: {job.error_stage || 'unknown'}
                                  </Typography>
                                  <Typography variant="body2">{job.error_message}</Typography>
                                </Alert>
                              </Grid>
                            )}
                          </Grid>
                        </Box>
                      </Collapse>
                    </TableCell>
                  </TableRow>
                </React.Fragment>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Info Footer */}
      <Box sx={{ mt: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Typography variant="caption" color="text.secondary">
          Showing {jobs.length} jobs from last 30 days
        </Typography>
        {stats?.avg_duration_seconds && (
          <Typography variant="caption" color="text.secondary">
            Avg. processing time: {formatDuration(stats.avg_duration_seconds)}
          </Typography>
        )}
      </Box>
    </Box>
  );
};

export default AgentCenter;
